import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import StatusBadge from '../../../components/shared/StatusBadge';
import { formatDate, formatCurrency } from '../../../utils/formatters';
import { Printer } from 'lucide-react';
import { printContent } from '../../../utils/print';

export default function RelatorioGestao() {
  const today = new Date().toISOString().split('T')[0];
  const d15 = new Date(Date.now() - 15 * 86400000).toISOString().split('T')[0];
  const [tab, setTab] = useState('pedidos');
  const [filters, setFilters] = useState({ data_inicio: d15, data_fim: today });
  const [tipoFiltro, setTipoFiltro] = useState('geral'); // 'geral' | 'cliente'
  const [clienteId, setClienteId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  useEffect(() => {
    api.get('/clientes').then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  const gerar = async () => {
    if (tipoFiltro === 'cliente' && !clienteId) { setError('Selecione um cliente'); return; }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ data_inicio: filters.data_inicio, data_fim: filters.data_fim });
      if (tipoFiltro === 'cliente' && clienteId) params.append('cliente_id', clienteId);
      const endpoint = tab === 'pedidos' ? 'gestao/pedidos' : 'gestao/produtos';
      const res = await api.get(`/relatorios/${endpoint}?${params}`);
      setData(res.data);
    } catch (err) { setError(err.userMessage || 'Erro ao gerar'); }
    finally { setLoading(false); }
  };

  const clienteSelecionado = clientes.find(c => c.id === clienteId);
  const inputCls = "rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid";

  return (
    <div data-testid="relatorio-gestao">
      <div className="flex items-center justify-between mb-6">
        <h1 className="dashboard-heading text-2xl">Relatórios de Gestão</h1>
        {data && <button onClick={() => printContent(tab === 'pedidos' ? 'Relatório de Gestão — Pedidos' : 'Relatório de Gestão — Produtos', printRef)} className="no-print flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm"><Printer className="w-4 h-4" /> Imprimir</button>}
      </div>

      <div className="card-dashboard mb-6 no-print">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[{ k: 'pedidos', l: 'Pedidos' }, { k: 'produtos', l: 'Produtos Vendidos' }].map(t => (
            <button key={t.k} onClick={() => { setTab(t.k); setData(null); }} className={`px-4 py-2 rounded-lg text-sm font-dashboard font-semibold ${tab === t.k ? 'bg-brand-primary text-white' : 'border border-brand-border text-brand-text-muted'}`} data-testid={`tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {/* Geral / Por cliente */}
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="tipoFiltro" value="geral" checked={tipoFiltro === 'geral'} onChange={() => { setTipoFiltro('geral'); setClienteId(''); setData(null); }} data-testid="radio-geral" />
            <span className="text-sm font-body font-medium text-brand-text">Geral</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="tipoFiltro" value="cliente" checked={tipoFiltro === 'cliente'} onChange={() => { setTipoFiltro('cliente'); setData(null); }} data-testid="radio-por-cliente" />
            <span className="text-sm font-body font-medium text-brand-text">Por Cliente</span>
          </label>
        </div>

        {/* Seletor de cliente */}
        {tipoFiltro === 'cliente' && (
          <div className="mb-4">
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Selecionar Cliente *</label>
            <select className={`w-full ${inputCls}`} value={clienteId} onChange={e => setClienteId(e.target.value)} data-testid="select-cliente-relatorio">
              <option value="">— Selecione —</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_razao}</option>)}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Data Início</label><input type="date" className={`w-full ${inputCls}`} value={filters.data_inicio} onChange={e => setFilters(f => ({ ...f, data_inicio: e.target.value }))} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Data Fim</label><input type="date" className={`w-full ${inputCls}`} value={filters.data_fim} onChange={e => setFilters(f => ({ ...f, data_fim: e.target.value }))} /></div>
        </div>
        <button onClick={gerar} disabled={loading} className="py-2.5 px-6 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="gerar-gestao-btn">
          {loading ? 'Gerando...' : 'Gerar Relatório'}
        </button>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>

      {data && (
        <div ref={printRef} className="print-content">
          {tipoFiltro === 'cliente' && clienteSelecionado && (
            <div className="mb-3 p-3 bg-brand-accent-light rounded-xl text-sm font-body text-brand-text">
              Cliente: <strong>{clienteSelecionado.nome_razao}</strong>
            </div>
          )}
          {tab === 'pedidos' ? (
            <>
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-brand-border">
                <h2 className="font-dashboard font-bold text-lg text-brand-text">Pedidos — {data.data_inicio} a {data.data_fim}</h2>
                <div className="text-right">
                  <p className="font-body text-sm text-brand-text-muted">{data.total_pedidos} pedido(s)</p>
                  <p className="font-dashboard font-bold text-brand-primary">{formatCurrency(data.total_faturamento)}</p>
                </div>
              </div>
              <table className="w-full text-sm font-body border border-brand-border">
                <thead className="bg-brand-bg"><tr>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">Pedido</th>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">Cliente</th>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">Data</th>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">Status</th>
                  <th className="text-right py-2 px-3 text-xs font-dashboard">Total</th>
                </tr></thead>
                <tbody>
                  {data.pedidos?.map((p, i) => (
                    <tr key={p.id} className={`border-t border-brand-border ${i % 2 === 0 ? '' : 'bg-brand-bg/50'}`}>
                      <td className="py-2 px-3 font-semibold text-brand-primary">#{p.numero}</td>
                      <td className="py-2 px-3 text-brand-text">{p.cliente_nome || '–'}</td>
                      <td className="py-2 px-3 text-brand-text-muted">{formatDate(p.data_pedido)}</td>
                      <td className="py-2 px-3"><StatusBadge status={p.status} /></td>
                      <td className="py-2 px-3 text-right font-semibold">{p.total != null ? formatCurrency(p.total) : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <h2 className="font-dashboard font-bold text-lg text-brand-text mb-4">Produtos Mais Vendidos — {data.data_inicio} a {data.data_fim}</h2>
              <table className="w-full text-sm font-body border border-brand-border">
                <thead className="bg-brand-bg"><tr>
                  <th className="text-center py-2 px-3 text-xs font-dashboard">#</th>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">SKU</th>
                  <th className="text-left py-2 px-3 text-xs font-dashboard">Produto</th>
                  <th className="text-center py-2 px-3 text-xs font-dashboard">Qtd. Total</th>
                  <th className="text-right py-2 px-3 text-xs font-dashboard">Valor Total</th>
                </tr></thead>
                <tbody>
                  {data.produtos?.map((p, i) => (
                    <tr key={p.produto_id} className={`border-t border-brand-border ${i % 2 === 0 ? '' : 'bg-brand-bg/50'}`}>
                      <td className="py-2 px-3 text-center font-bold text-brand-text-muted">{i + 1}</td>
                      <td className="py-2 px-3 font-mono text-xs text-brand-text-muted">{p.sku}</td>
                      <td className="py-2 px-3 font-medium text-brand-text">{p.descricao}</td>
                      <td className="py-2 px-3 text-center font-bold text-brand-primary">{p.quantidade_total}</td>
                      <td className="py-2 px-3 text-right font-semibold">{formatCurrency(p.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
